/*
 * iOS devices page (SPEC decision 28): pure model, no React. Where each device stands with Apple, how many of each
 * class the org registered against Apple's yearly limit, the facts line under a name, and the state of a registration
 * link the sheet is waiting on.
 */
import type { Device, DeviceList } from '@/api/endpointsInstall';

export type Tone = 'succeeded' | 'awaiting' | 'failed' | 'muted';

export interface AppleStatusView {
  label: string;
  tone: Tone;
  /** One sentence for the tooltip / line under the chip. */
  hint: string;
}

/** The Apple chip of a registered device. */
export function appleStatusOf(d: Pick<Device, 'status' | 'apple'>): AppleStatusView {
  if (d.status === 'awaiting_device') return { label: 'Waiting for the device', tone: 'awaiting', hint: 'Open the registration link on the iPhone or iPad.' };
  if (d.apple.status === 'registered') {
    return d.apple.simulated
      ? { label: 'Registered (simulated)', tone: 'succeeded', hint: 'This Hangar simulates Apple: local development, nothing was sent to Apple.' }
      : { label: 'Registered with Apple', tone: 'succeeded', hint: 'Internal iOS builds made from now on include this device.' };
  }
  if (d.apple.status === 'failed') {
    const message = d.apple.registrations.find((r) => r.status === 'failed')?.message;
    return { label: 'Apple refused it', tone: 'failed', hint: message ? `Apple said: ${message}` : 'Apple refused the device. Remove it and register it again.' };
  }
  return { label: 'Not at Apple yet', tone: 'muted', hint: 'Hangar registers it with Apple once your team\'s App Store Connect key is set up (ask your Hangar administrators).' };
}

const CLASS_LABEL: Record<string, string> = { iphone: 'iPhone', ipad: 'iPad', ipod: 'iPod touch', watch: 'Apple Watch', tv: 'Apple TV', vision: 'Apple Vision Pro', mac: 'Mac', other: 'Other' };
export const classLabel = (c: string | null | undefined): string => CLASS_LABEL[c ?? 'other'] ?? 'Other';

export interface ClassCount {
  deviceClass: string;
  label: string;
  count: number;
  limit: number;
}

/** Registered devices per class, against Apple's limit; iPhone and iPad always show, the rest when present. */
export function classCounts(list: Pick<DeviceList, 'data' | 'limits'>): ClassCount[] {
  const counts = new Map<string, number>([['iphone', 0], ['ipad', 0]]);
  for (const d of list.data) if (d.status === 'registered') counts.set(d.deviceClass ?? 'other', (counts.get(d.deviceClass ?? 'other') ?? 0) + 1);
  return [...counts].map(([deviceClass, count]) => ({ deviceClass, label: classLabel(deviceClass), count, limit: list.limits.devicesPerClassPerYear }));
}

/** "iPhone15,2 · iOS 22B83 · registered 28 Oct" pieces (the date is formatted by the caller). */
export function deviceFacts(d: Pick<Device, 'product' | 'osVersion' | 'deviceClass'>): string[] {
  return [d.product ?? classLabel(d.deviceClass), d.osVersion ? `iOS build ${d.osVersion}` : null].filter((x): x is string => Boolean(x));
}

/** A waiting row whose link already expired (the sweep removes it a day later). */
export const linkExpired = (d: Pick<Device, 'status' | 'linkExpiresAt'>, now: number): boolean => d.status === 'awaiting_device' && (!d.linkExpiresAt || Date.parse(d.linkExpiresAt) <= now);

export type RegistrationState = 'waiting' | 'registered' | 'expired' | 'gone';

/** What the registration sheet shows for the row it created, as the list refreshes. */
export function registrationState(d: Device | undefined, now: number): RegistrationState {
  if (!d) return 'gone';
  if (d.status === 'registered') return 'registered';
  return linkExpired(d, now) ? 'expired' : 'waiting';
}

/** Whether any registration still waits for its device (the list then refreshes every few seconds). */
export const anyWaiting = (devices: readonly Device[] | undefined, now: number): boolean => (devices ?? []).some((d) => registrationState(d, now) === 'waiting');
